import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, TimePicker, Row, Col, Button } from "antd";
import useKeyword from "../../../../hooks/useKeyword";
import { getEnrollClassForAddSchedule, getEnrollClassForEditSchedule } from "../../../../service/test-schedule";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import { viLocale as customLocale } from "../../../../utils/pickerType";

const { RangePicker } = TimePicker;

interface TestScheduleModalProps {
	open: boolean;
	mode: "create" | "view" | "edit";
	scheduleData?: TestScheduleFormValues;
	onCancel: () => void;
	onSave: (values: TestScheduleFormValues) => void;
	onEdit?: () => void;
}

interface TestScheduleFormValues {
	name: string;
	date: dayjs.Dayjs;
	timeRange: [dayjs.Dayjs, dayjs.Dayjs];
	location: string;
	applyTo: string;
	enrollClassData: any;
	id: number;
	timeStart: any;
	timeEnd: any;
}

interface ClassOption {
	id: number;
	name: string;
	disabled: boolean;
}

const TestScheduleModal: React.FC<TestScheduleModalProps> = ({ open, mode, scheduleData, onCancel, onSave, onEdit }) => {
  const { t } = useTranslation();
	const [form] = Form.useForm<TestScheduleFormValues>();
	const [enrollClassForAddSchedule, setEnrollClassForAddSchedule] = useState<ClassOption[]>([]);
	const unitId = useKeyword("unitId");

	useEffect(() => {
		const fetchEnrollClassForAddSchedule = async () => {
			try {
				const paramUrl = mode === "create"
					? getEnrollClassForAddSchedule(unitId)
					: getEnrollClassForEditSchedule(Number(unitId), Number(scheduleData?.id));

				const res = await paramUrl;
				setEnrollClassForAddSchedule(res?.data.data?.map((it: any) => ({
					value: it.id,
					label: it.name,
				})));
			} catch (error) {
				console.error("Lỗi khi tải danh sách lớp", error);
			}
		};
		fetchEnrollClassForAddSchedule();

		if ((mode === "edit" || mode === "view") && scheduleData) {
			setTimeout(() => {
				form.setFieldsValue({
					...scheduleData,
					date: dayjs(scheduleData.timeStart),
					timeRange: [dayjs(scheduleData.timeStart), dayjs(scheduleData.timeEnd)],
					applyTo: scheduleData.enrollClassData.map((it: any) => it.id),
				});
			}, 100);
		}


		else {
			form.resetFields();
		}
	}, [unitId, mode, scheduleData, form, open]);

	const onFinish = (values: TestScheduleFormValues) => {
		onSave(values);
	};

	const disabledDate = (current: dayjs.Dayjs) => {
		return current && current.isBefore(dayjs().startOf("day"));
	};
	const disabledTime = (selectedDate: dayjs.Dayjs | null) => {
		if (!selectedDate) return {};
		const isToday = selectedDate.isSame(dayjs(), "day");

		if (isToday) {
			const currentHour = dayjs().hour();
			const currentMinute = dayjs().minute();

			return {
				disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
				disabledMinutes: (hour: number) =>
					hour === currentHour ? Array.from({ length: currentMinute }, (_, i) => i) : [],
			};
		}

		return {};
	};
	const isPastSchedule = scheduleData
		? dayjs(scheduleData.timeEnd).isBefore(dayjs())
		: false;

	return (
		<Modal
			centered
			title={
				<div className="flex justify-between align-center mr-3">
					<div>{mode === "create" ? t("Thêm mới lịch", "Thêm mới lịch") : mode === "edit" ? t("Chỉnh sửa lịch", "Chỉnh sửa lịch") : t("Thông tin lịch", "Thông tin lịch")}</div>
					{mode === "view" && (
						<Button key="edit" onClick={onEdit} icon={<EditOutlined />}
							disabled={isPastSchedule}
						>
							{t("Sửa", "Sửa")}
						</Button>
					)}
				</div>
			}
			open={open}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					{t("Hủy", "Hủy")}
				</Button>,
				<Button key="submit" type="primary" onClick={() => form.submit()}
					disabled={mode === "view"}
				>
					{t("Lưu", "Lưu")}
				</Button>,
			]}
		>
			<Form form={form} name="testScheduleForm" onFinish={onFinish} layout="vertical" disabled={mode === "view"}>
				<Form.Item name="name" label={t("Tên", "Tên")} rules={[{ required: true, message: t("Vui lòng nhập tên lịch", "Vui lòng nhập tên lịch") }]}>
					<Input placeholder={t("Nhập tên lịch", "Nhập tên lịch")} allowClear />
				</Form.Item>

				<Form.Item label={<span><span className="text-danger">*</span>{t("&nbsp;Thời gian tổ chức", "&nbsp;Thời gian tổ chức")}</span>}>
					<Row gutter={8}>
						<Col span={12}>
							<Form.Item name="date" noStyle rules={[{ required: true, message: t("Chọn ngày tổ chức", "Chọn ngày tổ chức") }]}>
								<DatePicker
									format={"DD/MM/YYYY"}
									placeholder={t("Ngày", "Ngày")}
									style={{ width: "100%" }}
									locale={customLocale}
									disabledDate={disabledDate}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="timeRange"
								noStyle
								rules={[{ required: true, message: t("Chọn khoảng thời gian", "Chọn khoảng thời gian") }]}
							>
								<RangePicker
									placeholder={["Bắt đầu", "Kết thúc"]}
									format="HH:mm"
									locale={customLocale}
									disabledTime={() => disabledTime(form.getFieldValue("date"))}

								/>
							</Form.Item>
						</Col>
					</Row>
				</Form.Item>

				<Form.Item name="location" label={t("Địa điểm tổ chức", "Địa điểm tổ chức")} rules={[{ required: true, message: t("Vui lòng nhập địa điểm tổ chức", "Vui lòng nhập địa điểm tổ chức") }]}>
					<Input placeholder={t("Nhập địa điểm tổ chức", "Nhập địa điểm tổ chức")} allowClear />
				</Form.Item>

				<Form.Item name="applyTo" label={t("Áp dụng với", "Áp dụng với")}

					rules={[{ required: true, message: t("Vui lòng chọn lớp áp dụng", "Vui lòng chọn lớp áp dụng") }]}>
					<Select mode="multiple" allowClear options={enrollClassForAddSchedule}
						placeholder={t("Chọn lớp áp dụng", "Chọn lớp áp dụng")}

					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default TestScheduleModal;
